import { create } from 'zustand';
import { combine } from 'zustand/middleware';

const defaultRooms = [
  {
    id: 1,
    title: 'Комната 1'
  },
  {
    id: 2,
    title: 'Комната 2'
  }
];

type Room = {
  id: number;
  title: string;
  isEditing?: boolean;
};
type Updater<T> = T[] | ((prev: T[]) => T[]);

type ActiveView = 'participants' | 'chat' | 'docs' | '';

interface Meeting {
  uuid: string;
  title: string;
  author_uuid: string;
  is_practice_mode: boolean;
  status: number;
  assistant_join_uuid: string;
  created_time: string;
  finished_time: string | null;
}

interface MeetingState {
  meeting?: Meeting;
  currentlyViewingUserUid: string | null;
  rooms: Room[];
  handUpStatus: boolean;
  handUpStudents: string[];
  handUpRooms: string[];
  studentNotifications: number;
  activeToolPanel: ActiveView;
}

interface MeetingActions {
  setMeeting: (meeting: Meeting) => void;
  setMeetingTitle: (title: string) => void;
  resetMeeting: () => void;
  setCurrentlyViewingUserUid: (userUid: string | null) => void;
  addRoom: (id: number, title: string) => Room;
  setRooms: (updater: Updater<Room>) => void;
  resetDefaultRooms: () => void;
  setInitialRooms: (rooms: Room[]) => void;
  setHandUpRooms: (updater: Updater<string>) => void;
  setHandUpStatus: (handUpStatus: boolean) => void;
  setHandUpStudents: (updater: Updater<string>) => void;
  resetStudentNotifications: () => void;
  incrementStudentNotifications: () => void;
  decreaseStudentNotifications: () => void;
  setActiveToolPanel: (activeToolPanel: ActiveView) => void;
}

export const useMeetingStore = create(
  combine<MeetingState, MeetingActions>(
    {
      meeting: undefined,
      currentlyViewingUserUid: null,
      rooms: [],
      handUpStatus: false,
      handUpStudents: [],
      handUpRooms: [],
      studentNotifications: 0,
      activeToolPanel: 'participants'
    },
    (set) => ({
      setMeeting: (meeting) => set({ meeting }),
      setMeetingTitle: (title) =>
        set((state) => ({
          meeting: state.meeting
            ? {
                ...state.meeting,
                title
              }
            : state.meeting
        })),
      resetMeeting: () =>
        set({
          meeting: undefined,
          currentlyViewingUserUid: null,
          rooms: [],
          handUpStudents: [],
          studentNotifications: 0
        }),
      setCurrentlyViewingUserUid: (userUid) => set({ currentlyViewingUserUid: userUid }),
      addRoom: (id, title) => {
        const room = { id, title };
        set((state) => ({ rooms: [...state.rooms, room] }));
        return room;
      },
      setRooms: (updater) =>
        set((state) => ({ rooms: typeof updater === 'function' ? updater(state.rooms) : updater })),
      resetDefaultRooms: () => set({ rooms: [...defaultRooms] }),
      setInitialRooms: (rooms) => set({ rooms }),
      setHandUpRooms: (updater) =>
        set((state) => ({
          handUpRooms: typeof updater === 'function' ? updater(state.handUpRooms) : updater
        })),
      setHandUpStatus: (handUpStatus) => set({ handUpStatus }),
      setHandUpStudents: (updater) =>
        set((state) => ({
          handUpStudents: typeof updater === 'function' ? updater(state.handUpStudents) : updater
        })),
      resetStudentNotifications: () => set({ studentNotifications: 0 }),
      incrementStudentNotifications: () =>
        set((state) => ({
          studentNotifications: state.studentNotifications + 1
        })),
      decreaseStudentNotifications: () =>
        set((state) => ({
          studentNotifications: state.studentNotifications - 1
        })),
      setActiveToolPanel: (activeToolPanel) => set({ activeToolPanel })
    })
  )
);
